import React from "react";
import DamageImage from "./DamageImage";
import { formatPhone } from "../utility/formatPhone";
import "./GarageDetail.css";

function formatReportDateTime(timestamp) {
    if (!timestamp) return "-";

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

function formatStatusDuration(totalSeconds) {
    const normalizedSeconds = Number(totalSeconds);
    if (!Number.isFinite(normalizedSeconds) || normalizedSeconds < 0) {
        return "-";
    }

    const roundedSeconds = Math.round(normalizedSeconds);
    const hours = Math.floor(roundedSeconds / 3600);
    const minutes = Math.floor((roundedSeconds % 3600) / 60);
    const seconds = roundedSeconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }

    if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
}

function formatDamageReportActor(row) {
    const first = String(row?.userFirstName || "").trim();
    const last = String(row?.userLastName || "").trim();

    if (first && last) {
        return `${first.charAt(0).toUpperCase()}. ${last}`;
    }

    return row?.username || "Customer";
}

const DamageReportReview = ({ report, isLoading = false, error = "", emptyMessage = "No damage report available for this transaction." }) => {
    if (isLoading) {
        return <div className="damage-report-state">Loading report...</div>;
    }

    if (error) {
        return <div className="damage-report-state damage-report-state-error">{error}</div>;
    }

    if (!report) {
        return <div className="damage-report-state">{emptyMessage}</div>;
    }

    return (
        <div className="damage-report-layout">
            <div className="damage-report-summary-grid">
                <div className="damage-report-card">
                    <div className="damage-report-card-label">Ticket</div>
                    <div className="damage-report-card-value">{report?.vehicle?.ticketNum || "-"}</div>
                </div>
                <div className="damage-report-card">
                    <div className="damage-report-card-label">Status</div>
                    <div className="damage-report-card-value">{report?.visit?.latestStatusDescription || "-"}</div>
                </div>
                <div className="damage-report-card">
                    <div className="damage-report-card-label">Location</div>
                    <div className="damage-report-card-value">{report?.visit?.locationName || "-"}</div>
                </div>
                <div className="damage-report-card">
                    <div className="damage-report-card-label">Podium</div>
                    <div className="damage-report-card-value">{report?.visit?.podiumName || "-"}</div>
                </div>
                <div className="damage-report-card">
                    <div className="damage-report-card-label">Check-In</div>
                    <div className="damage-report-card-value">{formatReportDateTime(report?.visit?.checkinTime)}</div>
                </div>
                <div className="damage-report-card">
                    <div className="damage-report-card-label">Generated</div>
                    <div className="damage-report-card-value">{formatReportDateTime(report?.generatedAt)}</div>
                </div>
            </div>

            <div className="damage-report-section">
                <div className="damage-report-section-title">Vehicle</div>
                <div className="damage-report-detail-grid">
                    <div>
                        <strong>Make:</strong> {report?.vehicle?.make || "-"}
                    </div>
                    <div>
                        <strong>Color:</strong> {report?.vehicle?.color || "-"}
                    </div>
                    <div>
                        <strong>Mobile:</strong> {report?.vehicle?.mobile ? formatPhone(report.vehicle.mobile) : "-"}
                    </div>
                </div>
            </div>

            <div className="damage-report-section">
                <div className="damage-report-section-title">Damage Notes</div>
                <div className="damage-report-notes">{report?.damageEvidence?.notes || "No damage notes recorded."}</div>
            </div>

            <div className="damage-report-section">
                <div className="damage-report-section-title">Status Timeline ({Number(report?.transactionHistory?.entryCount || 0)})</div>

                {Array.isArray(report?.transactionHistory?.entries) && report.transactionHistory.entries.length > 0 ? (
                    <div className="damage-report-timeline-list">
                        {report.transactionHistory.entries.map((entry, index) => (
                            <div
                                key={entry.transactionId || `${entry?.statusId || "status"}-${entry?.transactionTime || index}`}
                                className="damage-report-timeline-row">
                                <div className="damage-report-timeline-status">
                                    <strong>{entry?.statusDescription || `Status ${entry?.statusId || "-"}`}</strong>
                                    <span>{formatReportDateTime(entry?.transactionTime)}</span>
                                </div>
                                <div className="damage-report-timeline-meta">
                                    <span>By: {formatDamageReportActor(entry)}</span>
                                    <span>{entry?.podiumName ? `Podium: ${entry.podiumName}` : "Podium: -"}</span>
                                    <span>Time In Status: {formatStatusDuration(entry?.durationInStatusSeconds)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="damage-report-photo-empty">No status history available for this visit.</div>
                )}

                {Array.isArray(report?.transactionHistory?.possessionStatusDurations) && report.transactionHistory.possessionStatusDurations.length > 0 ? (
                    <div className="damage-report-duration-summary">
                        {report.transactionHistory.possessionStatusDurations.map((row) => (
                            <div
                                key={`duration-${row.statusId}`}
                                className="damage-report-duration-card">
                                <div className="damage-report-duration-label">{row.statusDescription || `Status ${row.statusId}`}</div>
                                <div className="damage-report-duration-value">{formatStatusDuration(row.totalDurationSeconds)}</div>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>

            <div className="damage-report-section">
                <div className="damage-report-section-title">Damage Marks ({Number(report?.damageEvidence?.markCount || 0)})</div>
                <div className="damage-report-mark-wrap">
                    <DamageImage
                        damagePositions={Array.isArray(report?.damageEvidence?.marks) ? report.damageEvidence.marks : []}
                        className="damage-report-image"
                    />
                </div>
            </div>

            <div className="damage-report-section">
                <div className="damage-report-section-title">Damage Photos ({Number(report?.damageEvidence?.photoCount || 0)})</div>
                {Array.isArray(report?.damageEvidence?.photos) && report.damageEvidence.photos.length > 0 ? (
                    <div className="damage-report-photo-grid">
                        {report.damageEvidence.photos.map((photo) => (
                            <div
                                key={photo.id}
                                className="damage-report-photo-card">
                                {photo.url ? (
                                    <a
                                        href={photo.url}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        <img
                                            src={photo.url}
                                            alt="Damage evidence"
                                            className="damage-report-photo-thumb"
                                        />
                                    </a>
                                ) : (
                                    <div className="damage-report-photo-empty">Preview unavailable</div>
                                )}
                                <div className="damage-report-photo-meta">{photo.originalFileName || "Damage photo"}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="damage-report-photo-empty">No damage photos for this visit.</div>
                )}
            </div>
        </div>
    );
};

export default DamageReportReview;
